import React from "react";
import { v4 as uuidv4 } from "uuid";

import {
  createOutboundMessage,
  TCreateOutboundMessageResponse,
} from "../../../../../services/api-communications";
import { TSession } from "../../../../../utils/types";
import { toUserFacingErrorMessage } from "../utils/errors";

type TFailedAttempt = {
  conversationUuid: string | null;
  fromE164: string;
  toE164: string;
  body: string;
  clientRequestId: string;
};

type UseMessageComposerParams = {
  session: TSession | null | undefined;
  conversationUuid: string | null;
  toE164: string | null;
  patientUuid?: string | null;
  fromE164: string;
  draftBody: string;
  onDraftBodyChange: (value: string) => void;
  onOptimisticQueued?: (params: {
    conversationUuid: string;
    clientRequestId: string;
    fromE164: string;
    toE164: string;
    body: string;
  }) => void;
  onSendSuccess?: (params: {
    response: TCreateOutboundMessageResponse;
    clientRequestId: string;
  }) => void;
  onSendFailure?: (params: { clientRequestId: string; reason: string }) => void;
};

const normalizeDraft = (value: string): string => value.replace(/\s+$/g, "");

export const useMessageComposer = ({
  session,
  conversationUuid,
  toE164,
  patientUuid,
  fromE164,
  draftBody,
  onDraftBodyChange,
  onOptimisticQueued,
  onSendSuccess,
  onSendFailure,
}: UseMessageComposerParams) => {
  const [submitting, setSubmitting] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);
  const lastFailedAttemptRef = React.useRef<TFailedAttempt | null>(null);

  const sendMessage = React.useCallback(async () => {
    const normalizedConversationUuid = (conversationUuid || "").trim() || null;
    const normalizedToE164 = (toE164 || "").trim();
    const normalizedFromE164 = (fromE164 || "").trim();
    const normalizedBody = normalizeDraft((draftBody || "").trim());

    if (!session) {
      setError("You must be signed in to send messages.");
      return;
    }

    if (!normalizedToE164) {
      setError("Enter a recipient number before sending.");
      return;
    }

    if (!normalizedFromE164) {
      setError("Select a from number before sending.");
      return;
    }

    if (!normalizedBody) {
      setError("Message body cannot be empty.");
      return;
    }

    if (submitting) {
      return;
    }

    const failedAttempt = lastFailedAttemptRef.current;
    const canReuseFailedAttempt =
      !!failedAttempt &&
      failedAttempt.conversationUuid === normalizedConversationUuid &&
      failedAttempt.fromE164 === normalizedFromE164 &&
      failedAttempt.toE164 === normalizedToE164 &&
      failedAttempt.body === normalizedBody;

    const clientRequestId = canReuseFailedAttempt
      ? (failedAttempt as TFailedAttempt).clientRequestId
      : uuidv4();

    setSubmitting(true);
    setError(null);

    try {
      if (normalizedConversationUuid) {
        onOptimisticQueued?.({
          conversationUuid: normalizedConversationUuid,
          clientRequestId,
          fromE164: normalizedFromE164,
          toE164: normalizedToE164,
          body: normalizedBody,
        });
      }

      const response = await createOutboundMessage(session, {
        client_request_id: clientRequestId,
        from_e164: normalizedFromE164,
        to_e164: normalizedToE164,
        body: normalizedBody,
        ...(patientUuid
          ? {
              patient_uuid: patientUuid,
            }
          : {}),
      });

      lastFailedAttemptRef.current = null;
      onDraftBodyChange("");
      onSendSuccess?.({
        response,
        clientRequestId,
      });
    } catch (sendError) {
      const nextError = toUserFacingErrorMessage(
        sendError,
        "Unable to send this message right now.",
      );
      setError(nextError);
      lastFailedAttemptRef.current = {
        conversationUuid: normalizedConversationUuid,
        fromE164: normalizedFromE164,
        toE164: normalizedToE164,
        body: normalizedBody,
        clientRequestId,
      };
      onSendFailure?.({
        clientRequestId,
        reason: nextError,
      });
    } finally {
      setSubmitting(false);
    }
  }, [
    conversationUuid,
    draftBody,
    fromE164,
    onDraftBodyChange,
    onOptimisticQueued,
    onSendFailure,
    onSendSuccess,
    patientUuid,
    session,
    submitting,
    toE164,
  ]);

  React.useEffect(() => {
    if (conversationUuid) {
      return;
    }

    setError(null);
    setSubmitting(false);
    lastFailedAttemptRef.current = null;
  }, [conversationUuid]);

  return {
    submitting,
    error,
    sendMessage,
    clearError: () => setError(null),
  };
};

export default useMessageComposer;
